import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface BatchTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (startNumber: number, endNumber: number, capacity: number) => Promise<any>;
}

export function BatchTableDialog({ open, onOpenChange, onSubmit }: BatchTableDialogProps) {
  const [startNumber, setStartNumber] = useState("");
  const [endNumber, setEndNumber] = useState("");
  const [capacity, setCapacity] = useState("4");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const start = parseInt(startNumber);
    const end = parseInt(endNumber);
    const cap = parseInt(capacity) || 4;

    if (isNaN(start) || isNaN(end)) {
      toast.error("Informe números válidos");
      return;
    }

    if (start > end) {
      toast.error("O número inicial deve ser menor ou igual ao final");
      return;
    }

    if (end - start > 100) {
      toast.error("Máximo de 100 mesas por vez");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(start, end, cap);
      setStartNumber("");
      setEndNumber("");
      setCapacity("4");
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const count = (() => {
    const start = parseInt(startNumber);
    const end = parseInt(endNumber);
    if (isNaN(start) || isNaN(end) || start > end) return 0;
    return end - start + 1;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Criar Mesas em Lote</DialogTitle>
          <DialogDescription>
            Crie várias mesas de uma vez especificando um intervalo de números.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startNumber">De (número)</Label>
              <Input
                id="startNumber"
                type="number"
                min="1"
                value={startNumber}
                onChange={(e) => setStartNumber(e.target.value)}
                placeholder="1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endNumber">Até (número)</Label>
              <Input
                id="endNumber"
                type="number"
                min="1"
                value={endNumber}
                onChange={(e) => setEndNumber(e.target.value)}
                placeholder="10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacidade padrão</Label>
            <Input
              id="capacity"
              type="number"
              min="1"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="4"
            />
          </div>

          {count > 0 && (
            <p className="text-sm text-muted-foreground text-center">
              Serão criadas <strong>{count}</strong> mesas (de {startNumber} até {endNumber})
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || count === 0}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Criando...
              </>
            ) : (
              `Criar ${count} Mesas`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
